import type { PickerRow, UidGroupRow } from 'api/canonicalPicker.types'
import { picksOf, SourceSelection } from 'slices/aiSegmentBuilder/picker'

/** A group is picked whole (its global), through some of its store UIDs, or not at all */
export type GroupState = 'picked' | 'mixed' | 'none'

const isStoreUidOf =
  (group: UidGroupRow) =>
  (row: PickerRow): boolean =>
    row.kind === 'storeUid' &&
    row.provider === group.provider &&
    (group.storeUids ?? []).includes(row.uid)

export const groupStateOf = (
  group: UidGroupRow,
  selection: SourceSelection
): GroupState => {
  if (group.id in selection.rows) return 'picked'
  return picksOf(selection).some(isStoreUidOf(group)) ? 'mixed' : 'none'
}

/** The picks without any store UID of the given groups */
const withoutStoreUidsOf = (
  picks: PickerRow[],
  groups: UidGroupRow[]
): PickerRow[] =>
  picks.filter(row => !groups.some(group => isStoreUidOf(group)(row)))

const groupsAmong = (rows: PickerRow[]): UidGroupRow[] =>
  rows.filter((row): row is UidGroupRow => row.kind === 'uidGroup')

/**
 * Ticks or unticks a group. A group and its own store UIDs are never picked
 * together: ticking the group replaces them, as the group already holds them.
 */
export const toggledGroup = (
  picks: PickerRow[],
  group: UidGroupRow
): PickerRow[] =>
  picks.some(row => row.id === group.id)
    ? picks.filter(row => row.id !== group.id)
    : [...withoutStoreUidsOf(picks, [group]), group]

/**
 * Select all over the rows of a page, never the store UIDs inside a group:
 * ticks every row not picked yet, replacing the store UIDs of the groups it
 * ticks, or, when all of them are picked, unticks them and their store UIDs.
 */
export const toggledPage = (
  picks: PickerRow[],
  pageRows: PickerRow[]
): PickerRow[] => {
  const picked = new Set(picks.map(row => row.id))
  const pageIds = new Set(pageRows.map(row => row.id))
  const isAllPicked =
    pageRows.length > 0 && pageRows.every(row => picked.has(row.id))
  if (isAllPicked) {
    return withoutStoreUidsOf(
      picks.filter(row => !pageIds.has(row.id)),
      groupsAmong(pageRows)
    )
  }
  const added = pageRows.filter(row => !picked.has(row.id))
  return [...withoutStoreUidsOf(picks, groupsAmong(added)), ...added]
}
